/**
 * device.js (r7.4) — the device identity behind device-bound QRs.
 *
 * Native (Capacitor): @capacitor/device getId() — stable per install (Android: ANDROID_ID,
 * iOS: identifierForVendor). Web: a random UUID kept in localStorage — weaker (cleared with
 * site data), so events with device_binding 'strict' refuse web and the app shows why.
 *
 * Every API request carries X-SNT-Device / X-SNT-Platform (api/client.js interceptor); the
 * server stores only sha256(id) and puts a 16-hex prefix into the QR payload.
 */
import { Capacitor } from '@capacitor/core';

const KEY = 'snt:device_id';
let cached = null;
let pending = null;

function uuid() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => { const r = (Math.random() * 16) | 0; return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16); });
}

export function platform() {
  try { return Capacitor?.isNativePlatform?.() ? Capacitor.getPlatform() : 'web'; } catch { return 'web'; }
}

/** Resolve (and cache) the device id. Never throws. */
export async function getDeviceId() {
  if (cached) return cached;
  if (pending) return pending;
  pending = (async () => {
    let id = null;
    if (platform() !== 'web') {
      try {
        const { Device } = await import('@capacitor/device');
        const r = await Device.getId();
        id = r?.identifier || r?.uuid || null;
      } catch { id = null; }
    }
    if (!id) {
      try {
        id = localStorage.getItem(KEY);
        if (!id) { id = `web-${uuid()}`; localStorage.setItem(KEY, id); }
      } catch { id = `mem-${uuid()}`; }
    }
    cached = id;
    return id;
  })();
  return pending;
}

/** Synchronous accessor for the interceptor — null until getDeviceId() resolved once. */
export function deviceIdSync() { return cached; }

export function deviceHeaders() {
  const id = cached;
  return id ? { 'X-SNT-Device': id, 'X-SNT-Platform': platform() } : { 'X-SNT-Platform': platform() };
}

/** Kick the async resolution at app boot so the first ticket fetch already has it. */
export function initDevice() { getDeviceId().catch(() => {}); }
